import {
    Form, Params,
    redirect,
    useActionData, useLoaderData,
    useNavigate,
    useSubmit
} from "react-router-dom";
import {
    Button,
    Input,
    Message,
    Modal,
    Table, TableBody, TableCell,
    TableHeader,
    TableRow
} from "semantic-ui-react";
import * as React from "react";
import LabeledInput from "../../../components/labeled-input/labeled-input";
import LabeledTextArea from "../../../components/labeled-textarea/labeled-textarea";
import "./compensation-edit.css"
import { RootStore } from "../../../stores/root-store";
import { TransportError } from "../../../stores/data-transporter";
import { Compensation, CompensationDtoFactory } from "../../../stores/compensation/compensation-store";
import { getInt } from "../../../globals/form-data";
import LabeledValue from "../../../components/labeled-value/labeled-value";
import { CreationProject, UsageProject } from "../../../stores/project/project";
import { Constants } from "../../../globals/constants";
import { getLabel } from "../../../reflection/reflection";

type LoadResult = {
    usageProject: UsageProject;
    compensation: Compensation;
}

export async function loader(
    params: Params,
    rootStore: RootStore
) {
    if (params.compensationId !== undefined) {
        const compensationId = parseInt(params.compensationId) || 0;
        const rootStoreSession = rootStore.newSession();
        await rootStoreSession.municipalityStore.loadAll();
        const compensation = await rootStoreSession.compensationStore.load(compensationId);
        const usageProject = await rootStoreSession.projectStore.loadUsageProject(compensation.usageProjectId);
        await rootStoreSession.areaStore.loadUsageAreas({ projectId: usageProject.id });
        await rootStoreSession.projectStore.loadCreationProject(compensation.creationProjectId);
        await rootStoreSession.areaStore.loadCreationAreas({ projectId: compensation.creationProjectId });
        return { usageProject, compensation };
    }

    throw new Response("", {
        status: 404,
        statusText: "Kompensation wurde nicht gefunden",
    });
}

export async function action(request: Request, rootStore: RootStore) {
    const formData = await request.formData();
    const compensationDto = CompensationDtoFactory.makeCompensationDto(formData);

    if (compensationDto.area !== null) {
        const maxArea = getInt(formData, "maxArea");

        if (isNaN(compensationDto.area)) {
            return `Die Kompensationsfläche muss ausgefüllt sein.`;
        }

        if (compensationDto.area < 0) {
            return "Die Kompensationsfläche muss positiv sein."
        }

        if (compensationDto.area > maxArea) {
            return `Die Kompensationsfläche muss kleiner oder gleich der maximal verfügbaren Restfläche ${maxArea} sein.`;
        }

        if (Math.ceil(compensationDto.area) !== compensationDto.area) {
            return `Die Kompensationsfläche muss ganzzahlig sein.`;
        }
    }

    try {
        const rootStoreSession = rootStore.session;
        const compensation = await rootStoreSession.compensationStore.update(compensationDto);
        return redirect(`/projekte/${Constants.projectCategoryUsage}/${compensation.usageProjectId}`);
    } catch (e: Error) {
        if (e instanceof TransportError) {
            switch (e.statusCode) {
                case 400:
                    return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
                default:
                    return "Es ist ein unerwarteter Fehler aufgetreten!";
            }
        } else {
            return e.message;
        }
    }
}

export default function CompensationEdit(): JSX.Element {
    const { usageProject, compensation } = useLoaderData() as LoadResult;
    const errorMsg = useActionData() as any;
    const submit = useSubmit();
    const navigate = useNavigate();

    return (
        <Modal open={true} size="large">
            <Modal.Header>Kompensation bearbeiten</Modal.Header>
            <Modal.Content scrolling={true}>
                <Table compact="very" size="small" stackable={true}>
                    <TableHeader>
                        <TableRow verticalAlign="top">
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "id")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "municipalityName")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "fullType")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "ebageNr")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "name")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "status")}</Table.HeaderCell>
                            <Table.HeaderCell>{getLabel(CreationProject.prototype, "enactmentDateDisplayText")}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right">Restfläche</Table.HeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow>
                            <TableCell>{compensation.creationProject.id}</TableCell>
                            <TableCell>{compensation.creationProject.municipalityName}</TableCell>
                            <TableCell>{compensation.creationProject.type}</TableCell>
                            <Table.Cell>{compensation.creationProject.ebageNr}</Table.Cell>
                            <TableCell>{compensation.creationProject.name}</TableCell>
                            <TableCell>{compensation.creationProject.status}</TableCell>
                            <TableCell>{compensation.creationProject.enactmentDateDisplayText}</TableCell>
                            <TableCell textAlign="right">
                                {compensation.creationProject.totalAreaAvailable + compensation.area}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Form method="post" id="editCompensationForm">
                    <div className="compensationEdit">
                        <LabeledValue label="Total zu kompensierende Restfläche"
                                      bold={true}
                                      value={usageProject.totalAreaToCompensate + compensation.area}/>
                        <LabeledInput label="Kompensationsfläche" inputElement={<Input type="number" name="area"
                                                                                       defaultValue={compensation.area}/>}/>
                        <LabeledTextArea label="Bemerkungen" name="remarks"
                                         defaultValue={compensation.remarks}/>
                        {errorMsg !== undefined && <Message negative>{errorMsg}</Message>}
                        <input type="hidden" name="id"
                               value={compensation.id}/>
                        <input type="hidden" name="fffUsageProjectId"
                               value={compensation.usageProjectId}/>
                        <input type="hidden" name="fffCreationProjectId"
                               value={compensation.creationProjectId}/>
                        <input type="hidden" name="maxArea"
                               value={compensation.creationProject.totalAreaAvailable + compensation.area}/>
                    </div>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={() => navigate(-1)}>Abbrechen</Button>
                <Button
                    onClick={() => submit(document.getElementById("editCompensationForm") as HTMLFormElement)}>Speichern</Button>
            </Modal.Actions>
        </Modal>
    );
}